import React, { useCallback, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import { FiDownload } from 'react-icons/fi';
import { Document } from 'types/Invitation';
import { isPdfDocument } from 'utils/downloadFileFromBase64';

export type Folder = {
  type: 'folder';
  label: string;
  value: string;
  children: TreeNode[];
};

export type TreeNode = Folder | Document;

/** Type Guard para saber si es carpeta */
const isFolder = ( node: TreeNode ): node is Folder => typeof node === 'object' && 'type' in node && node.type === 'folder';

/** Type Guard para saber si es documento */
const isDocument = ( node: TreeNode ): node is Document => typeof node === 'object' && 'documentoUid' in node;

/** Construye el árbol desde un nivel */
export function buildDocumentFolderTreeFromLevel(
  documents: Document[],
  level: number,
): TreeNode[] {
  const root: TreeNode[] = [];

  documents.forEach( ( doc ) => {
    const parts = decodeURIComponent( doc.path || '' )
      .split( '/' )
      .filter( Boolean )
      .slice( level );

    if ( parts.length === 0 ) {
      root.push( doc );
      return;
    }

    let current = root;

    parts.forEach( ( part, index ) => {
      const isLast = index === parts.length - 1;

      if ( isLast ) {
        current.push( doc );
        return;
      }

      const existing = current.find(
        ( node ): node is Folder => isFolder( node ) && node.label === part,
      );

      if ( existing ) {
        current = existing.children;
      } else {
        const newFolder: Folder = {
          type: 'folder',
          label: part,
          value: parts.slice( 0, index + 1 ).join( '/' ),
          children: [],
        };
        current.push( newFolder );
        current = newFolder.children;
      }
    } );
  } );

  return root;
}

/** Componente recursivo para mostrar el árbol */
type RenderProps = {
  nodes: TreeNode[];
  pdfUrls: Record<string, string>;
  handleViewDocument: ( uid: string, token: string, name: string ) => void;
  setCurrentPdfUrl: ( url: string ) => void;
  setShowPdfModal: ( show: boolean ) => void;
  expandedFolders: Set<string>;
  onToggleFolder: ( folderValue: string ) => void;
};

export function RenderDocumentFolderTree( {
  nodes,
  pdfUrls,
  handleViewDocument,
  setCurrentPdfUrl,
  setShowPdfModal,
  expandedFolders,
  onToggleFolder,
  depth = 0,
}: RenderProps & { depth?: number } ) {
  const indent = '\u00A0\u00A0\u00A0\u00A0'.repeat( depth ); // 2 × &nbsp;

  return (
    <>
      {nodes.map( ( node ) => {
        if ( isFolder( node ) ) {
          const isExpanded = expandedFolders.has( node.value );

          return (
            <React.Fragment key={node.value}>
              <Row className="mt-3">
                <Col>
                  <button
                    type="button"
                    className="text--black1 d-flex align-items-center gap-1 mb-0 border-0 bg-transparent p-0 text-start h5"
                    onClick={() => onToggleFolder( node.value )}
                  >
                    <span dangerouslySetInnerHTML={{ __html: indent }} />
                    {isExpanded ? <BsChevronDown size={14} /> : <BsChevronRight size={14} />}
                    {node.label}
                  </button>
                </Col>
              </Row>
              {isExpanded && (
                <RenderDocumentFolderTree
                  nodes={node.children}
                  pdfUrls={pdfUrls}
                  handleViewDocument={handleViewDocument}
                  setCurrentPdfUrl={setCurrentPdfUrl}
                  setShowPdfModal={setShowPdfModal}
                  expandedFolders={expandedFolders}
                  onToggleFolder={onToggleFolder}
                  depth={depth + 1}
                />
              )}
            </React.Fragment>
          );
        }

        if ( isDocument( node ) ) {
          return (
            <Row key={node.documentoUid} className="mt-2 align-items-center">
              <Col>
                <div className="text--xmd text--italic">
                  <span dangerouslySetInnerHTML={{ __html: indent }} />
                  {node.nombre}
                </div>
              </Col>
              <Col xs="auto" className="d-flex">
                {node.tokenDescarga && (
                  <Button
                    variant="link"
                    className="p-0"
                    title={isPdfDocument( null, node.nombre ) ? 'Ver' : 'Descargar'}
                    onClick={() => {
                      if ( !node.documentoUid || !node.tokenDescarga ) return;
                      if ( pdfUrls[node.documentoUid] ) {
                        setCurrentPdfUrl( pdfUrls[node.documentoUid] );
                        setShowPdfModal( true );
                      } else {
                        handleViewDocument( node.documentoUid, node.tokenDescarga, node.nombre );
                      }
                    }}
                  >
                    {isPdfDocument( null, node.nombre ) ? (
                      <i className="icon-eye-line" style={{ fontSize: '1.5rem' }} />
                    ) : (
                      <FiDownload size={24} />
                    )}
                  </Button>
                )}
              </Col>
            </Row>
          );
        }

        return null;
      } )}
    </>
  );
}

type DocumentFolderTreeProps = Omit<RenderProps, 'expandedFolders' | 'onToggleFolder'>;

export function DocumentFolderTree( props: DocumentFolderTreeProps ) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>( () => new Set() );

  const onToggleFolder = useCallback( ( folderValue: string ) => {
    setExpandedFolders( ( prev ) => {
      const next = new Set( prev );
      if ( next.has( folderValue ) ) {
        next.delete( folderValue );
      } else {
        next.add( folderValue );
      }
      return next;
    } );
  }, [] );

  return (
    <RenderDocumentFolderTree
      {...props}
      expandedFolders={expandedFolders}
      onToggleFolder={onToggleFolder}
    />
  );
}
